/**
 // One controller per component/view lifecycle
const ac = new AbortController();

const avatar = html<HTMLImageElement>`<img class="avatar" src="${user.avatarUrl}" alt="${user.name}" />`;

const card = html<HTMLDivElement>`
    <div class="result-card">
        ${avatar}
        <p class="title">${item.Title}</p>
        <p class="desc">${item.Meta_Description ?? ''}</p>
    </div>
`;

on(card, 'click', () => openDetail(item), { signal: ac.signal });
on(card, ['mouseenter', 'mouseleave'], handleHover, { signal: ac.signal, passive: true });

// On component teardown / navigation away:
ac.abort(); // removes ALL listeners registered with this signal at once

 */

// ─── XSS-safe escape for string interpolations ───────────────────────────────

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;');
}

// ─── Trusted wrapper — opt-in raw HTML, explicit at call site ────────────────

const TRUSTED = Symbol('trusted');

interface TrustedHTML {
    [TRUSTED]: true;
    raw: string;
}

/** Explicitly mark a string as trusted raw HTML. Opt-in, not the default. */
export function trusted(raw: string): TrustedHTML {
    return { [TRUSTED]: true, raw };
}

// ─── Valid interpolation types ────────────────────────────────────────────────

type Interpolation =
    string | number | boolean | null | undefined | HTMLElement | DocumentFragment | TrustedHTML | Interpolation[]; // arrays are flattened

// ─── html ─────────────────────────────────────────────────────────────────────

const PLACEHOLDER_ATTR = 'data-html-slot';
// Use a stable but collision-resistant key
const uid = () => Math.random().toString(36).slice(2, 9);

/**
 * Tagged template literal that builds a typed HTMLElement.
 *
 * - Strings are HTML-escaped by default (XSS-safe)
 * - Wrap with `trusted(str)` to inject raw HTML deliberately
 * - HTMLElement / DocumentFragment values are inserted as real DOM nodes
 * - Arrays of any of the above are flattened
 *
 * @example
 * const label = userInput; // potentially unsafe
 * const el = html<HTMLButtonElement>`<button class="btn">${label}</button>`;
 *
 * @example
 * const icon = html`<svg>...</svg>`;
 * const card = html<HTMLDivElement>`<div class="card">${icon}</div>`;
 */
export function html<T extends HTMLElement = HTMLElement>(
    strings: TemplateStringsArray,
    ...values: Interpolation[]
): T {
    const nodeSlots = new Map<string, Node>();

    function serializeValue(value: Interpolation): string {
        if (value == null || value === false) return '';
        if (value === true) return '';

        if (Array.isArray(value)) {
            return value.map(serializeValue).join('');
        }

        if (value instanceof HTMLElement || value instanceof DocumentFragment) {
            const key = uid();
            nodeSlots.set(key, value);
            // Placeholder span that will be replaced after parsing
            return `<span ${PLACEHOLDER_ATTR}="${key}"></span>`;
        }

        if (typeof value === 'object' && TRUSTED in value) {
            return value.raw;
        }

        return escapeHtml(String(value));
    }

    const raw = strings
        .reduce((acc, str, i) => {
            return acc + str + serializeValue(values[i]);
        }, '')
        .trim();

    if (!raw) {
        throw new Error('[html] Template produced an empty string');
    }

    const template = document.createElement('template');
    template.innerHTML = raw;

    // Swap placeholder spans for real nodes
    for (const [key, node] of nodeSlots) {
        const slot = template.content.querySelector(`[${PLACEHOLDER_ATTR}="${key}"]`);
        if (slot) slot.replaceWith(node);
    }

    const root = template.content.firstElementChild;
    if (!root) {
        throw new Error(`[html] Template must have exactly one root element. Got:\n${raw}`);
    }

    return root as T;
}

// ─── on ───────────────────────────────────────────────────────────────────────

interface OnFn {
    <T extends HTMLElement, K extends keyof HTMLElementEventMap>(
        el: T,
        event: K | K[],
        handler: (e: HTMLElementEventMap[K]) => void,
        options?: OnOptions,
    ): T;
    <T extends HTMLElement, K extends keyof HTMLElementEventMap>(
        el: NodeListOf<T>,
        event: K | K[],
        handler: (e: HTMLElementEventMap[K]) => void,
        options?: OnOptions,
    ): NodeListOf<T>;
    <T extends HTMLElement, K extends keyof HTMLElementEventMap>(
        el: T[],
        event: K | K[],
        handler: (e: HTMLElementEventMap[K]) => void,
        options?: OnOptions,
    ): T[];
    <K extends keyof DocumentEventMap>(
        el: Document,
        event: K | K[],
        handler: (e: DocumentEventMap[K]) => void,
        options?: OnOptions,
    ): Document;
    <K extends keyof WindowEventMap>(
        el: Window,
        event: K | K[],
        handler: (e: WindowEventMap[K]) => void,
        options?: OnOptions,
    ): Window;
}

interface OnOptions {
    /** Pass an AbortSignal to remove the listener when the signal fires. */
    signal?: AbortSignal;
    /** Remove the listener after the first invocation. */
    once?: boolean;
    /** Mark the listener as passive (useful for scroll/touch perf). */
    passive?: boolean;
    /** Use capture phase. */
    capture?: boolean;
}

/**
 * Attaches one or more event listeners to a target and returns the same
 * target for chaining.
 *
 * Supported targets:
 * - HTMLElement
 * - NodeListOf<HTMLElement>
 * - HTMLElement[]
 * - Document
 * - Window
 *
 * Cleanup is handled via AbortController — create one controller per
 * component/view and call `.abort()` on teardown.
 *
 * @example
 * const ac = new AbortController();
 * on(button, 'click', handleClick, { signal: ac.signal });
 * on(input, 'input', handleInput, { signal: ac.signal, passive: true });
 *
 * // On teardown:
 * ac.abort();
 *
 * @example — chain multiple events on the same element
 * on(on(el, 'mouseenter', show), 'mouseleave', hide);
 */
export const on: OnFn = (
    el: HTMLElement | NodeListOf<HTMLElement> | HTMLElement[] | Document | Window,
    event: string | string[],
    handler: (e: Event) => void,
    options: OnOptions = {},
): HTMLElement | NodeListOf<HTMLElement> | HTMLElement[] | Document | Window => {
    const events = Array.isArray(event) ? event : [event];
    const isSingleTarget = (
        target: HTMLElement | NodeListOf<HTMLElement> | HTMLElement[] | Document | Window,
    ): target is HTMLElement | Document | Window => {
        return (
            target instanceof HTMLElement ||
            (typeof Document !== 'undefined' && target instanceof Document) ||
            (typeof Window !== 'undefined' && target instanceof Window)
        );
    };

    const elements: (HTMLElement | Document | Window)[] = isSingleTarget(el) ? [el] : Array.from(el);
    const { signal, once, passive, capture } = options;

    const listenerOptions = Object.fromEntries(
        Object.entries({ signal, once, passive, capture }).filter(([, v]) => v !== undefined),
    ) as AddEventListenerOptions;

    const resolvedOptions = Object.keys(listenerOptions).length > 0 ? listenerOptions : undefined;

    for (const element of elements) {
        for (const e of events) {
            element.addEventListener(e, handler as EventListener, resolvedOptions);
        }
    }

    return el;
};
